import { useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Accordion, AccordionDetails, AccordionSummary, Card, styled, Typography } from '@mui/material';
import { COLOR_DARK, TokenFormat } from 'shared';
import CopyField from 'shared/ui/copy-field';

import { wusdtToken } from '../../model';
import { StyledTextField } from '../styled';

import { SaleFormData } from './types';

type ReferralProps = {
  earned: bigint | undefined;
  address: string;
  purchased?: boolean;
};

const MyReferralLinkBlock = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2, 2, 3, 2),
  background: COLOR_DARK,
  border: 'none',
}));

const buildReferralLink = (address: string) => {
  const loc = globalThis?.location;
  if (!loc) return address;

  const url = new URL(loc.href);
  url.searchParams.set('citRef', address);
  return url.toString();
};

export const Referral = ({ address, earned, purchased }: ReferralProps) => {
  const [expanded, setExpanded] = useState(false);

  const { control, watch } = useFormContext<SaleFormData>();

  useEffect(() => {
    const sub = watch((values, { name }) => {
      if (name !== 'referrer') return;
      if (!values.referrer) return;

      setExpanded(true);
      sub.unsubscribe(); // subscribe once
    });
    return () => sub.unsubscribe();
  }, [watch]);

  return (
    <>
      <Accordion className="secondary" expanded={expanded}>
        <AccordionSummary onClick={() => setExpanded(!expanded)}>
          <Typography variant="h6" className="secondary">
            {purchased ? 'Your referral address' : 'Enter referral address'}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Controller
            name="referrer"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <StyledTextField
                {...field}
                placeholder="0x0000000000000000000000000000000000000000"
                fullWidth
                size="small"
                $error={!!error}
                disabled={purchased}
                helperText={error?.message}
              />
            )}
          />
        </AccordionDetails>
      </Accordion>
      <MyReferralLinkBlock sx={{ mt: 2 }}>
        <Typography variant="h6" className="primary regular">
          🎁{' '}
          <Typography component="span" className="accent" variant="inherit">
            Earn 10%
          </Typography>{' '}
          instantly in wUSDT of all purchases made by your referrals
        </Typography>

        <CopyField
          copyValue={buildReferralLink(address)}
          displayValue={address}
          sx={{ mt: 2, padding: '10px 16px', '& > p': { fontSize: '16px' } }}
        />

        {!!earned && (
          <Typography variant="h6" textAlign="center" className="secondary regular" mt={1.5}>
            Referral rewards earned:{' '}
            <TokenFormat
              amount={earned}
              decimals={wusdtToken.decimals}
              maxDecimalDigits={2}
              variant="inherit"
              className="primary"
              fallback={`0.00 ${wusdtToken.symbol}`}
              symbol={wusdtToken.symbol}
            />
          </Typography>
        )}
      </MyReferralLinkBlock>
    </>
  );
};
